import { createFileRoute } from '@tanstack/react-router'
import type { TKey } from '@/lib/i18n'
import { SITE_URL } from '@/lib/site'
import { buildSeo } from './-lib/seo'
import { createSiteTranslate } from './-lib/site-i18n'

export const Route = createFileRoute('/(public)/')({
	ssr: true,
	head: ({ match }) => {
		const { appLanguage } = match.context
		const t = createSiteTranslate(appLanguage)
		const seo = buildSeo({
			appLanguage,
			path: '/',
			title: t('site:seo.home.title'),
			description: t('site:seo.home.description')
		})

		return {
			...seo,
			scripts: [
				{
					type: 'application/ld+json',
					children: JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'MobileApplication',
						name: 'SeaLedger',
						description: t('site:seo.home.description'),
						url: SITE_URL,
						applicationCategory: 'UtilitiesApplication',
						operatingSystem: 'iOS, Android',
						offers: {
							'@type': 'Offer',
							price: '0',
							priceCurrency: 'USD'
						}
					})
				}
			]
		}
	},
	component: LandingPage
})

type Translate = (key: TKey) => string

// ─── Shared icons ─────────────────────────────────────────────────────────────

function CheckIcon() {
	return (
		<span className='ck'>
			<svg
				aria-hidden='true'
				fill='none'
				height='13'
				viewBox='0 0 24 24'
				width='13'
			>
				<path
					d='M5 13l4 4L19 7'
					stroke='oklch(78% 0.14 85)'
					strokeLinecap='round'
					strokeLinejoin='round'
					strokeWidth='2.4'
				/>
			</svg>
		</span>
	)
}

function ChevronIcon() {
	return (
		<span className='chev'>
			<svg
				aria-hidden='true'
				fill='none'
				height='18'
				viewBox='0 0 24 24'
				width='18'
			>
				<path
					d='M6 9l6 6 6-6'
					stroke='currentColor'
					strokeLinecap='round'
					strokeWidth='2'
				/>
			</svg>
		</span>
	)
}

// ─── Phone mockup (hero right column) ────────────────────────────────────────

function PhoneMockup(props: { t: Translate }) {
	const { t } = props
	return (
		<div className='mock-stage'>
			<div className='mock-glow' />
			<div className='phone'>
				<div className='di' />
				<div className='ph-top'>
					<span>9:41</span>
					<span>SeaLedger</span>
				</div>
				<div className='ph-body'>
					<div className='ph-h'>
						<span className='t'>
							{t('site:landing.hero.phone_collection')}
						</span>
						<span className='ph-pill'>68%</span>
					</div>
					<div className='ph-bar'>
						<i />
					</div>
					<div className='ph-grid'>
						<div className='ph-card own'>
							<span className='pip'>
								<i className='on' />
								<i className='on' />
								<i />
							</span>
						</div>
						<div className='ph-card own'>
							<span className='pip'>
								<i className='on' />
								<i />
								<i />
							</span>
						</div>
						<div className='ph-card'>
							<span className='pip'>
								<i />
								<i />
								<i />
							</span>
						</div>
						<div className='ph-card own'>
							<span className='pip'>
								<i className='on' />
								<i className='on' />
								<i className='on' />
							</span>
						</div>
						<div className='ph-card'>
							<span className='pip'>
								<i />
								<i />
								<i />
							</span>
						</div>
						<div className='ph-card own'>
							<span className='pip'>
								<i className='on' />
								<i />
								<i />
							</span>
						</div>
						<div className='ph-card soon' />
						<div className='ph-card own'>
							<span className='pip'>
								<i className='on' />
								<i className='on' />
								<i />
							</span>
						</div>
						<div className='ph-card'>
							<span className='pip'>
								<i />
								<i />
								<i />
							</span>
						</div>
					</div>
				</div>
				<div className='ph-tab'>
					<span className='act' />
					<span />
					<span />
					<span />
				</div>
			</div>
		</div>
	)
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function HeroSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section className='hero'>
			<div className='wrap hero-grid'>
				<div className='hero-copy'>
					<span className='eyebrow'>
						<svg
							aria-hidden='true'
							fill='none'
							height='13'
							viewBox='0 0 24 24'
							width='13'
						>
							<path
								d='M12 2l2.4 5.2L20 8l-4 4 1 6-5-2.8L7 18l1-6-4-4 5.6-.8L12 2z'
								fill='currentColor'
							/>
						</svg>
						{t('site:landing.hero.eyebrow')}
					</span>
					<h1>
						{t('site:landing.hero.title_lead')}{' '}
						<em>{t('site:landing.hero.title_em')}</em>
					</h1>
					<p className='lead'>{t('site:landing.hero.lead')}</p>
					<div className='hero-actions'>
						<a className='badge' href='#scarica'>
							<svg
								aria-hidden='true'
								fill='currentColor'
								height='24'
								viewBox='0 0 24 24'
								width='24'
							>
								<path d='M16.4 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.2.8-.6 0-1.7-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.4 2.5-.4 6.2 1 8.3.7 1 1.5 2.1 2.5 2.1 1 0 1.4-.6 2.6-.6 1.2 0 1.5.6 2.6.6 1.1 0 1.8-1 2.4-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.2zM14.5 6.3c.5-.7.9-1.6.8-2.5-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.7-.4 2.3-1.1z' />
							</svg>
							<span>
								<span className='b-sub'>
									{t('site:landing.hero.badge_apple_sub')}
								</span>
								<span className='b-main'>
									{t('site:landing.hero.badge_apple_main')}
								</span>
							</span>
						</a>
						<a className='badge' href='#scarica'>
							<svg
								aria-hidden='true'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<path
									d='M3.6 2.4c-.3.3-.5.8-.5 1.4v16.4c0 .6.2 1.1.5 1.4l.1.1 9.2-9.2v-.2L3.6 2.4z'
									fill='oklch(70% 0.13 235)'
								/>
								<path
									d='M16.3 15.4l-3.1-3.1v-.2l3.1-3.1.1.1 3.6 2.1c1 .6 1 1.6 0 2.2l-3.7 2z'
									fill='oklch(78% 0.14 85)'
								/>
								<path
									d='M16.4 15.3L13.2 12l-9.2 9.2c.3.4.9.4 1.5.1l10.9-6.2z'
									fill='oklch(64% 0.2 25)'
								/>
								<path
									d='M16.4 8.7L5.5 2.5c-.6-.3-1.2-.3-1.5.1l9.2 9.2 3.2-3.1z'
									fill='oklch(72% 0.16 150)'
								/>
							</svg>
							<span>
								<span className='b-sub'>
									{t('site:landing.hero.badge_google_sub')}
								</span>
								<span className='b-main'>
									{t('site:landing.hero.badge_google_main')}
								</span>
							</span>
						</a>
					</div>
					<div className='hero-meta'>
						<span>
							<b>{t('site:landing.hero.stat_rating_value')}</b>{' '}
							{t('site:landing.hero.stat_rating_label')}
						</span>
						<span className='dot' />
						<span>
							<b>{t('site:landing.hero.stat_db_value')}</b>{' '}
							{t('site:landing.hero.stat_db_label')}
						</span>
						<span className='dot' />
						<span>
							<b>{t('site:landing.hero.stat_langs')}</b>
						</span>
					</div>
				</div>
				<PhoneMockup t={t} />
			</div>
		</section>
	)
}

function HowItWorksSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section className='section' id='come-funziona'>
			<div className='wrap center'>
				<span className='eyebrow'>
					{t('site:landing.how_it_works.eyebrow')}
				</span>
				<h2 className='h-sec'>
					{t('site:landing.how_it_works.title')}
				</h2>
				<p className='sub-sec'>
					{t('site:landing.how_it_works.subtitle')}
				</p>
			</div>
			<div className='wrap'>
				<div className='steps'>
					<div className='step'>
						<div className='step-n'>01</div>
						<div className='step-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='26'
								viewBox='0 0 24 24'
								width='26'
							>
								<rect
									height='13'
									rx='3'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
									width='18'
									x='3'
									y='6'
								/>
								<circle
									cx='12'
									cy='12.5'
									r='3.2'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
								/>
								<path
									d='M8 6l1.2-2h5.6L16 6'
									stroke='oklch(78% 0.14 85)'
									strokeLinejoin='round'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.how_it_works.step_1_title')}</h3>
						<p>{t('site:landing.how_it_works.step_1_body')}</p>
					</div>
					<div className='step'>
						<div className='step-n'>02</div>
						<div className='step-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='26'
								viewBox='0 0 24 24'
								width='26'
							>
								<path
									d='M4 7h16M4 12h16M4 17h10'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeWidth='1.6'
								/>
								<circle
									cx='18'
									cy='17'
									r='3'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.how_it_works.step_2_title')}</h3>
						<p>{t('site:landing.how_it_works.step_2_body')}</p>
					</div>
					<div className='step'>
						<div className='step-n'>03</div>
						<div className='step-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='26'
								viewBox='0 0 24 24'
								width='26'
							>
								<rect
									height='15'
									rx='2'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
									width='11'
									x='4'
									y='3'
								/>
								<path
									d='M9 21h11V8'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.how_it_works.step_3_title')}</h3>
						<p>{t('site:landing.how_it_works.step_3_body')}</p>
					</div>
				</div>
			</div>
		</section>
	)
}

function FeaturesSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section
			className='section'
			id='funzionalita'
			style={{ paddingTop: '32px' }}
		>
			<div className='wrap center'>
				<span className='eyebrow'>
					{t('site:landing.features.eyebrow')}
				</span>
				<h2 className='h-sec'>{t('site:landing.features.title')}</h2>
				<p className='sub-sec'>{t('site:landing.features.subtitle')}</p>
			</div>
			<div className='wrap'>
				<div className='features'>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<rect
									height='13'
									rx='3'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
									width='18'
									x='3'
									y='6'
								/>
								<circle
									cx='12'
									cy='12.5'
									r='3'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.scanner_title')}</h3>
						<p>{t('site:landing.features.scanner_body')}</p>
					</div>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<path
									d='M4 6h16M4 12h16M4 18h16'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeWidth='1.6'
								/>
								<circle
									cx='8'
									cy='6'
									fill='oklch(78% 0.14 85)'
									r='1.6'
								/>
								<circle
									cx='15'
									cy='12'
									fill='oklch(78% 0.14 85)'
									r='1.6'
								/>
								<circle
									cx='11'
									cy='18'
									fill='oklch(78% 0.14 85)'
									r='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.collection_title')}</h3>
						<p>{t('site:landing.features.collection_body')}</p>
					</div>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<path
									d='M2.5 5h7M6.5 3.5v1.5M4 5c0 3-1.5 5-2.5 6M5 6c.5 2.5 2 4 3.5 4.5'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='1.5'
								/>
								<path
									d='M11 20l4-9 4 9M12.6 16.5h4.8'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='1.5'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.multilang_title')}</h3>
						<p>{t('site:landing.features.multilang_body')}</p>
					</div>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<rect
									height='15'
									rx='2'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
									width='11'
									x='4'
									y='3'
								/>
								<path
									d='M9 21h11V8'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.deck_title')}</h3>
						<p>{t('site:landing.features.deck_body')}</p>
					</div>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<path
									d='M12 20s-7-4.4-7-9.4A4 4 0 0112 8a4 4 0 017 2.6c0 5-7 9.4-7 9.4z'
									stroke='oklch(78% 0.14 85)'
									strokeLinejoin='round'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.wishlist_title')}</h3>
						<p>{t('site:landing.features.wishlist_body')}</p>
					</div>
					<div className='feat'>
						<div className='feat-ic'>
							<svg
								aria-hidden='true'
								fill='none'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<circle
									cx='11'
									cy='11'
									r='7'
									stroke='oklch(78% 0.14 85)'
									strokeWidth='1.6'
								/>
								<path
									d='M20 20l-3.5-3.5'
									stroke='oklch(78% 0.14 85)'
									strokeLinecap='round'
									strokeWidth='1.6'
								/>
							</svg>
						</div>
						<h3>{t('site:landing.features.search_title')}</h3>
						<p>{t('site:landing.features.search_body')}</p>
					</div>
				</div>
			</div>
		</section>
	)
}

function LanguagesSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section className='section' id='lingue' style={{ paddingTop: '32px' }}>
			<div className='wrap'>
				<div className='usp'>
					<div className='usp-copy'>
						<span className='eyebrow'>
							{t('site:landing.languages.eyebrow')}
						</span>
						<h2>{t('site:landing.languages.title')}</h2>
						<p>{t('site:landing.languages.body')}</p>
						<div className='usp-list'>
							<div className='usp-li'>
								<CheckIcon />
								<span>
									<b>
										{t(
											'site:landing.languages.list_1_label'
										)}
									</b>{' '}
									{t('site:landing.languages.list_1_text')}
								</span>
							</div>
							<div className='usp-li'>
								<CheckIcon />
								<span>
									<b>
										{t(
											'site:landing.languages.list_2_label'
										)}
									</b>{' '}
									{t('site:landing.languages.list_2_text')}
								</span>
							</div>
							<div className='usp-li'>
								<CheckIcon />
								<span>
									<b>
										{t(
											'site:landing.languages.list_3_label'
										)}
									</b>{' '}
									{t('site:landing.languages.list_3_text')}
								</span>
							</div>
						</div>
					</div>
					<div className='lens-demo'>
						<div className='lens-tabs'>
							<span className='lens-tab act'>
								{t('site:landing.languages.tab_all')}
							</span>
							<span className='lens-tab'>
								{t('site:landing.languages.tab_en')}
							</span>
							<span className='lens-tab'>
								{t('site:landing.languages.tab_jp')}
							</span>
							<span className='lens-tab'>
								{t('site:landing.languages.tab_fr')}
							</span>
						</div>
						<div className='lens-rows'>
							<div className='lens-row'>
								<div className='lg'>
									<span className='lens-flag'>EN</span>
									<span>
										{t('site:landing.languages.lang_en')}
									</span>
								</div>
								<div
									style={{
										alignItems: 'center',
										display: 'flex',
										gap: '8px'
									}}
								>
									<span className='lens-bar'>
										<i style={{ width: '82%' }} />
									</span>
									<span className='lens-pct'>82%</span>
								</div>
							</div>
							<div className='lens-row'>
								<div className='lg'>
									<span className='lens-flag'>JP</span>
									<span>
										{t('site:landing.languages.lang_jp')}
									</span>
								</div>
								<div
									style={{
										alignItems: 'center',
										display: 'flex',
										gap: '8px'
									}}
								>
									<span className='lens-bar'>
										<i style={{ width: '54%' }} />
									</span>
									<span className='lens-pct'>54%</span>
								</div>
							</div>
							<div className='lens-row'>
								<div className='lg'>
									<span className='lens-flag'>FR</span>
									<span>
										{t('site:landing.languages.lang_fr')}
									</span>
								</div>
								<div
									style={{
										alignItems: 'center',
										display: 'flex',
										gap: '8px'
									}}
								>
									<span className='lens-bar'>
										<i style={{ width: '31%' }} />
									</span>
									<span className='lens-pct'>31%</span>
								</div>
							</div>
							<div
								className='lens-row'
								style={{
									borderTop: '1px solid var(--border)',
									marginTop: '6px',
									paddingTop: '12px'
								}}
							>
								<div className='lg'>
									<b>
										{t('site:landing.languages.lang_total')}
									</b>
								</div>
								<div
									style={{
										alignItems: 'center',
										display: 'flex',
										gap: '8px'
									}}
								>
									<span className='lens-bar'>
										<i style={{ width: '68%' }} />
									</span>
									<span
										className='lens-pct'
										style={{ color: 'var(--accent)' }}
									>
										68%
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

function FaqSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section className='section' id='faq' style={{ paddingTop: '32px' }}>
			<div className='wrap center'>
				<span className='eyebrow'>{t('site:landing.faq.eyebrow')}</span>
				<h2 className='h-sec'>{t('site:landing.faq.title')}</h2>
			</div>
			<div className='wrap'>
				<div className='faq'>
					<details className='qa' open>
						<summary>
							{t('site:landing.faq.q1')}
							<ChevronIcon />
						</summary>
						<div className='ans'>{t('site:landing.faq.a1')}</div>
					</details>
					<details className='qa'>
						<summary>
							{t('site:landing.faq.q2')}
							<ChevronIcon />
						</summary>
						<div className='ans'>{t('site:landing.faq.a2')}</div>
					</details>
					<details className='qa'>
						<summary>
							{t('site:landing.faq.q3')}
							<ChevronIcon />
						</summary>
						<div className='ans'>{t('site:landing.faq.a3')}</div>
					</details>
					<details className='qa'>
						<summary>
							{t('site:landing.faq.q4')}
							<ChevronIcon />
						</summary>
						<div className='ans'>{t('site:landing.faq.a4')}</div>
					</details>
					<details className='qa'>
						<summary>
							{t('site:landing.faq.q5')}
							<ChevronIcon />
						</summary>
						<div className='ans'>{t('site:landing.faq.a5')}</div>
					</details>
				</div>
			</div>
		</section>
	)
}

function DownloadSection(props: { t: Translate }) {
	const { t } = props
	return (
		<section
			className='section'
			id='scarica'
			style={{ paddingTop: '32px' }}
		>
			<div className='wrap'>
				<div className='cta-band'>
					<h2>{t('site:landing.download.title')}</h2>
					<p>{t('site:landing.download.subtitle')}</p>
					<div className='badges'>
						<a className='badge' href='#scarica'>
							<svg
								aria-hidden='true'
								fill='currentColor'
								height='24'
								viewBox='0 0 24 24'
								width='24'
							>
								<path d='M16.4 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.2.8-.6 0-1.7-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.4 2.5-.4 6.2 1 8.3.7 1 1.5 2.1 2.5 2.1 1 0 1.4-.6 2.6-.6 1.2 0 1.5.6 2.6.6 1.1 0 1.8-1 2.4-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.2zM14.5 6.3c.5-.7.9-1.6.8-2.5-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.7-.4 2.3-1.1z' />
							</svg>
							<span>
								<span className='b-sub'>
									{t('site:landing.download.badge_apple_sub')}
								</span>
								<span className='b-main'>
									{t(
										'site:landing.download.badge_apple_main'
									)}
								</span>
							</span>
						</a>
						<a className='badge' href='#scarica'>
							<svg
								aria-hidden='true'
								height='22'
								viewBox='0 0 24 24'
								width='22'
							>
								<path
									d='M3.6 2.4c-.3.3-.5.8-.5 1.4v16.4c0 .6.2 1.1.5 1.4l.1.1 9.2-9.2v-.2L3.6 2.4z'
									fill='oklch(70% 0.13 235)'
								/>
								<path
									d='M16.3 15.4l-3.1-3.1v-.2l3.1-3.1.1.1 3.6 2.1c1 .6 1 1.6 0 2.2l-3.7 2z'
									fill='oklch(78% 0.14 85)'
								/>
								<path
									d='M16.4 15.3L13.2 12l-9.2 9.2c.3.4.9.4 1.5.1l10.9-6.2z'
									fill='oklch(64% 0.2 25)'
								/>
								<path
									d='M16.4 8.7L5.5 2.5c-.6-.3-1.2-.3-1.5.1l9.2 9.2 3.2-3.1z'
									fill='oklch(72% 0.16 150)'
								/>
							</svg>
							<span>
								<span className='b-sub'>
									{t(
										'site:landing.download.badge_google_sub'
									)}
								</span>
								<span className='b-main'>
									{t(
										'site:landing.download.badge_google_main'
									)}
								</span>
							</span>
						</a>
					</div>
				</div>
			</div>
		</section>
	)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function LandingPage() {
	const { appLanguage } = Route.useRouteContext()
	const t = createSiteTranslate(appLanguage)

	return (
		<>
			<HeroSection t={t} />
			<HowItWorksSection t={t} />
			<FeaturesSection t={t} />
			<LanguagesSection t={t} />
			<FaqSection t={t} />
			<DownloadSection t={t} />
		</>
	)
}
